import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Link,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CardPaymentForm from "./CardPaymentForm";
import InStorePaymentForm from "./InStorePaymentForm";
import { useAppConfig } from "../../../context/AppConfigContext";

const PaymentForm = ({ onPaymentSuccess, amount = 0, metadata = {} }) => {
  const { config } = useAppConfig();

  const [useInStorePayment, setUseInStorePayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showAcknowledgeError, setShowAcknowledgeError] = useState(false);

  useEffect(() => {
    metadata.setAcknowledgment?.(acknowledged);
  }, [acknowledged]);

  const policyMessages = [
    config?.cancellationPolicy?.types?.specialtyCake?.message,
    config?.cancellationPolicy?.types?.regular?.message,
  ].filter(Boolean);

  const handleAcknowledge = (checked) => {
    setAcknowledged(checked);
    if (checked) setShowAcknowledgeError(false);
  };

  const handleBlockAttempt = () => {
    if (!acknowledged) {
      setShowAcknowledgeError(true);
      return false;
    }
    return true;
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {useInStorePayment ? "Complete In-Store Payment" : "Enter Card Details"}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Link
          component="button"
          underline="always"
          onClick={() => setUseInStorePayment(!useInStorePayment)}>
          {useInStorePayment
            ? "← Go back to Card Payment"
            : "Use In-Store Payment (staff use only)"}
        </Link>
      </Box>

      {useInStorePayment ? (
        <InStorePaymentForm
          onPaymentSuccess={onPaymentSuccess}
          setErrorMessage={setErrorMessage}
          canProceed={acknowledged}
          onBlockedAttempt={handleBlockAttempt}
        />
      ) : (
        <CardPaymentForm
          onPaymentSuccess={onPaymentSuccess}
          amount={amount}
          metadata={{
            ...metadata,
            acknowledgedCancellationPolicy: acknowledged,
          }}
          setErrorMessage={setErrorMessage}
          canProceed={acknowledged}
          onBlockedAttempt={handleBlockAttempt}
        />
      )}

      {/* Single-line acknowledgment with collapsible policy */}
      <Box sx={{ mt: 4 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => handleAcknowledge(e.target.checked)}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: "#b23c17", fontWeight: 500, mr: 1 }}>
                I acknowledge the refund and cancellation terms
              </Typography>
              <IconButton
                onClick={() => setShowPolicy(!showPolicy)}
                size="small">
                {showPolicy ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />

        <Collapse in={showPolicy}>
          <Box
            sx={{
              mt: 1,
              backgroundColor: "#fffaf0",
              p: 2,
              borderRadius: 2,
            }}>
            {policyMessages.map((msg, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{ color: "#663c00", mb: 0.5 }}>
                ⚠️ {msg}
              </Typography>
            ))}

            <Typography variant="body2" sx={{ mt: 1 }}>
              <Link
                href="/refund-policy"
                underline="always"
                target="_blank"
                rel="noopener"
                sx={{ color: "#b23c17", fontWeight: 500 }}>
                View full refund policy here
              </Link>
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {showAcknowledgeError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Please acknowledge the refund and cancellation policy before
          proceeding.
        </Alert>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2 }}>
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PaymentForm;
